/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import React from "react";

import * as Sentry from "@sentry/react";

import { logger } from "./logger";

export class ErrorHandler {
    public static initialize(): void {
        // Only log in production, let React DevTools handle dev errors
        if (process.env.NODE_ENV !== "development") {
            // Auto-capture unhandled JavaScript errors
            window.addEventListener("error", event => {
                const error = event.error || new Error(event.message);
                logger.error("Unhandled JavaScript error", {
                    message: error.message,
                    stack: error.stack,
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                });
            });

            // Auto-capture unhandled promise rejections
            window.addEventListener("unhandledrejection", event => {
                logger.error("Unhandled promise rejection", {
                    reason: event.reason?.toString(),
                    stack: event.reason?.stack,
                });
            });
        }
    }
}

// React Error Boundary Component
export interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

export const AppErrorBoundary = Sentry.withErrorBoundary(
    ({ children }: { children: React.ReactNode }) => children,
    {
        fallback: ({
            error,
            resetError,
        }: {
            error: any;
            resetError: () => void;
        }) => (
            <div style={{ padding: "20px", textAlign: "center" }}>
                <h2>Something went wrong</h2>
                <p>{error?.message}</p>
                <button onClick={resetError}>Try again</button>
            </div>
        ),
        beforeCapture: (
            _scope: Sentry.Scope,
            error: any,
            componentStack: string,
        ) => {
            // This will automatically log React errors through our service
            logger.error("React Error Boundary triggered", {
                error: error.message,
                stack: error.stack,
                componentStack,
            });
        },
    },
);
